import { formatDisplayDateTime } from '@/utils/date';

/**
 * Pure presentational config + helpers for the seller shift. No React, no state
 * — just the shift's shared types, the cash-diff math (used by the
 * close-shift count), the dialog's header and footer config, and the cart's
 * gate notice. The `labelKey`s reference `pages` namespace strings that land
 * in the locale files in a later phase.
 */

export type ShiftStatus = 'closed' | 'open';

/**
 * Where the seller's own shift status stands (SEL-49): `GET /shifts/current`
 * has not answered yet (`loading`), its latest read was refused or never
 * arrived (`failed`, with a retry), or it answered (`known`). Only `known`
 * says whether a shift is open: before that, `isOpen` reads `false`, so
 * selling stays gated, but nothing may start a shift either.
 */
export type ShiftStatusRead =
    | { state: 'loading' }
    | { state: 'failed'; onRetry: () => void }
    | { state: 'known' };

/** Which action the shift button and its dialog perform, from the shift status. */
export type ShiftFlowMode = 'open' | 'close';

/**
 * Which page of the modal is showing: the count, the discrepancy co-sign, or
 * the server's record of a finished close.
 */
export type ShiftStep = 'count' | 'approve' | 'closed';

/**
 * What the current step's footer button does, and `Enter` with it: confirm
 * the blind count, go to the co-sign, open or close the shift, or dismiss the
 * finished close.
 */
export type ShiftStepAction =
    'confirmCount' | 'getApproval' | 'openShift' | 'closeShift' | 'done';

/**
 * Why this seller can't open a shift on this device (SEL-30), from the sale
 * point handover guard: another seller's shift, remembered here, is still open
 * (`blocked`); its status is being read for the first time (`checking`); or that
 * read failed (`unavailable`, with a retry). `null` when nothing stands in the
 * way.
 */
export type ShiftHandover =
    | { state: 'checking' }
    | { state: 'unavailable'; onRetry: () => void }
    | { state: 'blocked'; sellerName: string; openedAt: string };

/** How the counted drawer compares to what the system expects at close. */
export type DiffKind = 'match' | 'short' | 'over';

/**
 * `Intl` options for the cash-count field — GEL currency, matching how BosApp's
 * purchase product rows format money inputs. Lives here (not the `.tsx`) so the
 * render-only step component stays config-free.
 */
export const SHIFT_CASH_FORMAT: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'GEL',
};

/**
 * The parts of the close-side expected cash, in the order the count step lists
 * them. Keys match `ShiftCashBreakdown`.
 */
export const BREAKDOWN_ROWS = [
    { key: 'openingFloat', labelKey: 'seller.shift.openingFloat' },
    { key: 'cashSales', labelKey: 'seller.shift.cashSales' },
    { key: 'cashRefunds', labelKey: 'seller.shift.cashRefunds' },
    { key: 'payouts', labelKey: 'seller.shift.payouts' },
] as const;

/**
 * The shift dialog's header on each step: a `pages` title key and its icon.
 * The count step names the flow (open or close); the co-sign and the finished
 * close name themselves.
 */
export const shiftModalHeader = (
    step: ShiftStep,
    mode: ShiftFlowMode,
): { titleKey: string; icon: string } => {
    if (step === 'closed') {
        return {
            titleKey: 'seller.shift.closedTitle',
            icon: 'solar:check-circle-bold-duotone',
        };
    }
    if (step === 'approve') {
        return {
            titleKey: 'seller.shift.approvalTitle',
            icon: 'solar:shield-keyhole-bold-duotone',
        };
    }
    return mode === 'open'
        ? {
              titleKey: 'seller.shift.openTitle',
              icon: 'solar:play-circle-bold-duotone',
          }
        : {
              titleKey: 'seller.shift.closeTitle',
              icon: 'solar:stop-circle-bold-duotone',
          };
};

/**
 * The footer's primary button for each step action (`useShiftFlow`'s
 * `stepAction`): its `pages` label key and its test id. Opening and closing
 * share the submit button's id.
 */
export const SHIFT_STEP_ACTIONS: Record<
    ShiftStepAction,
    { labelKey: string; testId: string }
> = {
    confirmCount: {
        labelKey: 'seller.shift.confirmCount',
        testId: 'seller-shift-confirm-count',
    },
    getApproval: {
        labelKey: 'seller.shift.getApproval',
        testId: 'seller-shift-get-approval',
    },
    openShift: {
        labelKey: 'seller.shift.start',
        testId: 'seller-shift-submit',
    },
    closeShift: {
        labelKey: 'seller.shift.closeAction',
        testId: 'seller-shift-submit',
    },
    done: { labelKey: 'seller.shift.done', testId: 'seller-shift-done' },
};

/** Counted cash minus expected, rounded to 2dp (money never carries more). */
export const computeDiff = (expected: number, counted: number): number =>
    Math.round((counted - expected) * 100) / 100;

/**
 * Classify the drawer diff. A sub-cent gap counts as a `match` (floating-point
 * cash sums never land exactly on zero); otherwise `short` when the till holds
 * less than expected, `over` when it holds more.
 */
export const classifyDiff = (expected: number, counted: number): DiffKind => {
    const diff = computeDiff(expected, counted);
    if (Math.abs(diff) < 0.005) {
        return 'match';
    }
    return counted < expected ? 'short' : 'over';
};

/** The `pages` key for each handover state's cart notice; values come separately. */
const HANDOVER_NOTICE_KEYS: Record<ShiftHandover['state'], string> = {
    checking: 'seller.shift.handoverChecking',
    unavailable: 'seller.shift.handoverUnavailable',
    blocked: 'seller.shift.handoverNotice',
};

/** The cart's gate notice: a `pages` key, its values, and a retry for a read that failed. */
export type ShiftGateNoticeContent = {
    key: string;
    values?: { name: string; openedAt: string };
    onRetry?: () => void;
};

/**
 * The cart's notice while this seller has no shift open, or `null` while one
 * is. A failed read of the seller's own status comes first, with its retry
 * (SEL-49); then a failed sale point handover check (SEL-30), whose retry
 * stays in reach while the seller's status is still loading; then the status
 * being checked. Once it is known: the handover guard naming whose shift still
 * holds the drawer and when it opened, or the prompt to open one.
 */
export const shiftGateNotice = ({
    statusRead,
    isOpen,
    handover,
}: {
    statusRead: ShiftStatusRead;
    isOpen: boolean;
    handover: ShiftHandover | null;
}): ShiftGateNoticeContent | null => {
    if (statusRead.state === 'failed') {
        return {
            key: 'seller.shift.statusUnavailable',
            onRetry: statusRead.onRetry,
        };
    }
    if (statusRead.state === 'known' && isOpen) {
        return null;
    }
    if (handover?.state === 'unavailable') {
        return {
            key: HANDOVER_NOTICE_KEYS.unavailable,
            onRetry: handover.onRetry,
        };
    }
    if (statusRead.state === 'loading') {
        return { key: 'seller.shift.statusChecking' };
    }
    if (handover === null) {
        return { key: 'seller.shift.needShiftToSell' };
    }
    if (handover.state === 'blocked') {
        return {
            key: HANDOVER_NOTICE_KEYS.blocked,
            values: {
                name: handover.sellerName,
                openedAt: formatDisplayDateTime(handover.openedAt),
            },
        };
    }
    return { key: HANDOVER_NOTICE_KEYS[handover.state] };
};
